import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  TextInput,
  NumberInput,
  Toggle,
  RadioButtonGroup,
  RadioButton,
  RadioButtonSkeleton,
  InlineLoading,
  Dropdown,
  FormLabel,
  DatePicker,
  DatePickerInput
} from 'carbon-components-react';
import { Add16 } from '@carbon/icons-react';
import { useFormik } from 'formik';
import { FormError } from 'components';
import { NewOrganisationSchema } from 'utilities/validation/newOrganisation.validation';
import { EditOrganisationSchema } from 'utilities/validation/editOrganisation.validation';
import Utils from 'utilities/helper.util';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import {
  createOrganisationStart,
  updateOrganisationStart
} from '../../localStore/actions';
import { useComponentMount } from 'utilities/hooks.util';
import { LimitTooltip, TransactionLimitTooltip } from '../limitTooltip';

type NewOrgFormValues = {
  orgName: string;
  masterAdminFirstName: string;
  masterAdminLastName: string;
  masterAdminEmail: string;
  hasCreateEnquiry: boolean;
  orgGroup?: number;
  masterAdmin?: number;
  subscriptionStart?: string;
  subscriptionEnd?: string;
  contractRenewal?: string;
  userLimits?: number;
  teamLimits?: number;
};

const MAX_LIMIT_STEP = 10;
const MAX_LIMIT_MAX = 500;
const MAX_LIMIT_MIN = 0;

const NewOrganisationForm: React.FC<Props> = ({
  onClose,
  onOpenTransactionLimitsForm,
  setToastNotificationData,
  setShowNewOrgResponseModal,
  setEditData,
  editData
}) => {
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const [submitErrText, setSubmitErrText] = useState<string>('');
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [disableContractRenewalDate, setDisableContractRenewalDate] =
    useState<boolean>(true);
  const [disableSubsriptionEndDate, setDisableSubsriptionEndDate] =
    useState<boolean>(true);
  const apiCalled = useRef<boolean>(false);

  useComponentMount(() => {
    setToastNotificationData(null);
    setShowNewOrgResponseModal(false);
  });

  const getSelectedUserName = (
    type: string,
    adminUsers: any[],
    id?: number
  ) => {
    if (id) {
      const user = adminUsers.find((u) => u.value === id);
      if (user) {
        if (type === 'first') {
          return user.firstName;
        } else if (type === 'last') {
          return user.lastName;
        } else {
          return user.fullName;
        }
      } else {
        return '-';
      }
    } else {
      return '-';
    }
  };

  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    setFieldTouched,
    isSubmitting,
    setSubmitting,
    handleSubmit,
    setValues
  } = useFormik<NewOrgFormValues>({
    enableReinitialize: true,
    initialValues: editData
      ? {
          orgName: editData.organisation_name,
          masterAdminFirstName: getSelectedUserName(
            'first',
            adminUsers,
            editData.masterAdmin
          ),
          masterAdminLastName: getSelectedUserName(
            'last',
            adminUsers,
            editData.masterAdmin
          ),
          masterAdminEmail: editData?.Admin ? editData.Admin.reg_email : '',
          hasCreateEnquiry: !!editData.has_create_enquiry,
          userLimits: editData.Restrictions.find(
            (r: any) => r.restriction_type === 'ADD_USER'
          )?.Limits[0]?.amount,
          teamLimits: editData.Restrictions.find(
            (r: any) => r.restriction_type === 'ADD_TEAM'
          )?.Limits[0]?.amount,
          orgGroup:
            editData.OrganisationGroupSubscribers?.organisation_group_id ||
            undefined,
          masterAdmin: editData.master_admin || undefined,
          subscriptionStart:
            editData.OrganisationGroupSubscribers?.subscription_start || '',
          subscriptionEnd:
            editData.OrganisationGroupSubscribers?.subscription_end || '',
          contractRenewal:
            editData.OrganisationGroupSubscribers?.contract_renewal || ''
        }
      : {
          orgName: '',
          masterAdminFirstName: '',
          masterAdminLastName: '',
          masterAdminEmail: '',
          hasCreateEnquiry: true,
          subscriptionStart: '',
          subscriptionEnd: '',
          contractRenewal: ''
        },
    validationSchema: isEditMode
      ? EditOrganisationSchema
      : NewOrganisationSchema,
    onSubmit: (values: NewOrgFormValues) => {
      if (isEditMode) {
        const data = {
          organisation_name: values.orgName,
          organisation_group: values.orgGroup || null,
          master_admin: values.masterAdmin || null,
          has_create_enquiry: values.hasCreateEnquiry,
          subscription_start: values.subscriptionStart,
          subscription_end: values.subscriptionEnd,
          user_limits: values.userLimits,
          team_limits: values.teamLimits,
          contract_renewal: values.contractRenewal
        };
        localDispatch(updateOrganisationStart({ id: editData.id, data }));
      } else {
        const emailDomain = values.masterAdminEmail.split('@')[1];
        const payload = {
          organisationName: values.orgName,
          organisationGroup: values.orgGroup || null,
          hasCreateEnquiry: values.hasCreateEnquiry,
          emailDomain: `@${emailDomain}`,
          emailDomains: [emailDomain],
          User: {
            email: values.masterAdminEmail,
            firstName: values.masterAdminFirstName,
            lastName: values.masterAdminLastName
          },
          subscriptionStart: values.subscriptionStart,
          subscriptionEnd: values.subscriptionEnd,
          userLimits: Number(values.userLimits),
          teamLimits: Number(values.teamLimits),
          contractRenewal: values.contractRenewal
        };
        localDispatch(createOrganisationStart(payload));
      }
    }
  });

  const {
    createOrgsStatus,
    getOrgGroupsStatus,
    organisationGroups,
    createOrgsError,
    updateOrgStatus,
    updateOrgError
  } = localStore;

  const onCheckBoxChange = (value: boolean) => {
    setFieldTouched('hasCreateEnquiry', true);
    setFieldValue('hasCreateEnquiry', value);
  };

  const handleCancelClick = () => {
    setEditData(null);
    setIsEditMode(false);
    onClose();
  };

  const handleChangeText = (value: any) => {
    setFieldTouched('orgName', true);
    setFieldValue('orgName', value);
  };

  const getSelectedAdminUser = (adminUsers: any[], id?: number) => {
    if (id) {
      return adminUsers.find((u) => u.value === id);
    } else {
      return undefined;
    }
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    const newDate = Utils.getNewDate(val[0]).format('YYYY-MM-DD');
    setField && setFieldValue(key, newDate, true);
    setField && setFieldTouched(key, true);
  };

  const onNumberInputChange = (
    key: string,
    value: number,
    touched: boolean = false
  ) => {
    setFieldValue(key, value ? Number(value) : 0, touched);
    if (!touched) {
      setFieldTouched(key, true, false);
    }
  };

  useEffect(() => {
    if (editData) {
      setIsEditMode(true);
      if (Array.isArray(editData.Users) && editData.Users.length > 0) {
        const admins = editData.Users.filter(
          (u: any) => u.role_type === 'Organisation Admin'
        ).map((u: any) => ({
          fullname: `${u.first_name || ''} ${u.last_name || ''}`,
          firstName: u.first_name,
          lastName: u.last_name,
          value: u.id,
          label: u.reg_email
        }));
        setAdminUsers(admins);
      } else {
        setAdminUsers([]);
      }
    } else {
      setIsEditMode(false);
    }
  }, [editData, setValues]);

  useEffect(() => {
    if (createOrgsStatus === 'creating' || updateOrgStatus === 'updating') {
      apiCalled.current = true;
    }
    if (isEditMode) {
      if (isSubmitting && apiCalled.current && updateOrgStatus === 'initial') {
        apiCalled.current = false;
        if (submitErrText) {
          setSubmitting(false);
        } else {
          setEditData(null);
          setIsEditMode(false);
          onClose();
        }
      }
    } else {
      if (isSubmitting && apiCalled.current && createOrgsStatus === 'initial') {
        apiCalled.current = false;
        submitErrText ? setSubmitting(false) : onClose();
      }
    }
  }, [
    isSubmitting,
    createOrgsStatus,
    updateOrgStatus,
    setSubmitting,
    onClose,
    submitErrText,
    isEditMode,
    setEditData
  ]);

  useEffect(() => {
    if (createOrgsError) {
      setSubmitErrText(createOrgsError);
      setToastNotificationData({
        type: 'add',
        kind: 'error',
        subtitle: createOrgsError
      });
      setShowNewOrgResponseModal(true);
    }
  }, [
    createOrgsError,
    setShowNewOrgResponseModal,
    setToastNotificationData,
    values
  ]);

  useEffect(() => {
    if (updateOrgError) {
      setSubmitErrText(updateOrgError);
      setToastNotificationData({
        type: 'edit',
        kind: 'error',
        subtitle: updateOrgError
      });
      setShowNewOrgResponseModal(true);
    }
  }, [
    updateOrgError,
    setShowNewOrgResponseModal,
    setToastNotificationData,
    values
  ]);

  useEffect(() => {
    if (createOrgsStatus === 'success') {
      setToastNotificationData &&
        setToastNotificationData({
          type: 'add',
          kind: 'success',
          subtitle: `'${values.orgName}' successfully added`
        });
      setShowNewOrgResponseModal(true);
      onClose();
    }
  }, [
    createOrgsStatus,
    setToastNotificationData,
    values,
    setShowNewOrgResponseModal,
    onClose
  ]);

  useEffect(() => {
    if (updateOrgStatus === 'success') {
      setToastNotificationData &&
        setToastNotificationData({
          type: 'update',
          kind: 'success',
          subtitle: `'${values.orgName}' successfully edited`
        });
      setShowNewOrgResponseModal(true);
      setEditData(null);
      setIsEditMode(false);
    }
  }, [
    setToastNotificationData,
    values,
    setShowNewOrgResponseModal,
    updateOrgStatus,
    setEditData
  ]);

  useEffect(() => {
    if (values.subscriptionStart && values.contractRenewal) {
      setDisableContractRenewalDate(false);
    }

    if (values.subscriptionStart) {
      setDisableSubsriptionEndDate(false);
    }
  }, [values]);

  // comment out for now until this is ready to use
  /*
  let subscriptionStartMin = Utils.toLocalDateFormat(new Date());
  if (isEditMode) {
    if (editData?.OrganisationGroupSubscribers?.subscription_start) {
      const originalStartAt = Utils.getNewDate(
        editData?.OrganisationGroupSubscribers?.subscription_start
      ).startOf('day');
      const todayStartAt = Utils.getNewDate().startOf('day');
      if (todayStartAt > originalStartAt) {
        subscriptionStartMin = Utils.toLocalDateFormat(originalStartAt);
      }
    } else {
      subscriptionStartMin = '';
    }
  }
  */
  const subscriptionStartMax = values.subscriptionEnd
    ? Utils.getNewDateWithTimezone(
        values.subscriptionEnd,
        'Australia/Melbourne'
      ).format('DD/MM/YYYY')
    : '';
  const subscriptionEndMin = values.contractRenewal
    ? Utils.getNewDateWithTimezone(
        values.contractRenewal,
        'Australia/Melbourne'
      ).format('DD/MM/YYYY')
    : '';
  const contractRenewalMin = values.subscriptionStart
    ? Utils.getNewDate(values.subscriptionStart)
        .add(1, 'day')
        .format('DD/MM/YYYY')
    : Utils.getNewDateWithTimezone(new Date(), 'Australia/Melbourne').format(
        'DD/MM/YYYY'
      );

  const contractRenewalMax = values.subscriptionEnd
    ? Utils.getNewDateWithTimezone(
        values.subscriptionEnd,
        'Australia/Melbourne'
      ).format('DD/MM/YYYY')
    : '';

  return (
    <div className="sys-admin-org-new-form-modal">
      <div className="sys-admin-org-new-form-panel">
        <div className="header">
          {isEditMode ? 'Edit organisation' : 'Add new organisation'}
        </div>
        <div className="body">
          <div className="new-form-group">
            <TextInput
              id="orgName"
              name="orgName"
              labelText="Organisation name"
              data-testid="orgName"
              onChange={(evt: any) => handleChangeText(evt.target.value)}
              onBlur={handleBlur}
              value={values.orgName}
            />
            {errors.orgName && touched.orgName && (
              <FormError>{errors.orgName}</FormError>
            )}
          </div>
          {isEditMode && (
            <div className="new-form-group">
              <Dropdown
                id="email"
                size="lg"
                titleText="Master administrator email"
                label={adminUsers.length === 0 ? '' : 'Choose an option'}
                items={adminUsers}
                itemToString={(item: any) => item.label}
                onBlur={handleBlur}
                selectedItem={getSelectedAdminUser(
                  adminUsers,
                  values.masterAdmin
                )}
                onChange={({ selectedItem }) =>
                  setFieldValue('masterAdmin', selectedItem.value)
                }
                disabled={adminUsers.length === 0}
              />
            </div>
          )}
          <div className="new-form-group">
            <TextInput
              id="masterAdminFirstName"
              name="masterAdminFirstName"
              labelText="Master administrator first name"
              data-testid="masterAdminFirstName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={
                isEditMode
                  ? getSelectedUserName('first', adminUsers, values.masterAdmin)
                  : values.masterAdminFirstName
              }
              disabled={isEditMode}
            />
            {errors.masterAdminFirstName && touched.masterAdminFirstName && (
              <FormError>{errors.masterAdminFirstName}</FormError>
            )}
          </div>
          <div className="new-form-group">
            <TextInput
              id="masterAdminLastName"
              name="masterAdminLastName"
              labelText="Master administrator last name"
              data-testid="masterAdminLastName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={
                isEditMode
                  ? getSelectedUserName('last', adminUsers, values.masterAdmin)
                  : values.masterAdminLastName
              }
              disabled={isEditMode}
            />
            {errors.masterAdminLastName && touched.masterAdminLastName && (
              <FormError>{errors.masterAdminLastName}</FormError>
            )}
          </div>
          {!isEditMode && (
            <div className="new-form-group">
              <TextInput
                id="masterAdminEmail"
                name="masterAdminEmail"
                labelText="Master administrator email"
                data-testid="masterAdminEmail"
                placeholder="e.g : email@domain"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.masterAdminEmail}
                disabled={isEditMode}
              />
              {errors.masterAdminEmail && touched.masterAdminEmail && (
                <FormError>{errors.masterAdminEmail}</FormError>
              )}
            </div>
          )}
          <div className="new-form-group">
            {getOrgGroupsStatus === 'fetching' ? (
              <RadioButtonSkeleton />
            ) : (
              <RadioButtonGroup
                legendText="Organisation group"
                name="category"
                orientation="vertical"
                valueSelected={values.orgGroup}
                onChange={(value) => {
                  setFieldValue('orgGroup', Number(value), true);
                  setFieldTouched('orgGroup', true, false);
                }}
              >
                {organisationGroups.map((og, index) => (
                  <RadioButton
                    key={index}
                    labelText={og.name}
                    value={og.id}
                    id={`org-group-${og.id}`}
                  />
                ))}
              </RadioButtonGroup>
            )}
          </div>
          <div className="new-form-group subscription-date">
            <div className="date-picker-container">
              <DatePicker
                dateFormat="d/m/Y"
                datePickerType="single"
                className="date-picker"
                // minDate={subscriptionStartMin} // comment out temporarily
                maxDate={subscriptionStartMax}
                onChange={(val: any) => {
                  onChangeDate('subscriptionStart', val, true);
                }}
                value={
                  values.subscriptionStart
                    ? Utils.getNewDateWithTimezone(
                        values.subscriptionStart,
                        'Australia/Melbourne'
                      ).format('DD/MM/YYYY')
                    : ''
                }
              >
                <DatePickerInput
                  id="subscriptionStart"
                  name="subscriptionStart"
                  data-testid="subscriptionStart"
                  labelText="Subscription start date"
                  placeholder="dd/mm/yyyy"
                  type="text"
                  autoComplete="off"
                />
              </DatePicker>
              {errors.subscriptionStart && touched.subscriptionStart && (
                <FormError>{errors.subscriptionStart}</FormError>
              )}
            </div>
            <div className="date-picker-container">
              <DatePicker
                dateFormat="d/m/Y"
                datePickerType="single"
                className="date-picker"
                minDate={contractRenewalMin}
                maxDate={contractRenewalMax}
                onChange={(val: any) => {
                  onChangeDate('contractRenewal', val, true);
                }}
                value={
                  values.contractRenewal
                    ? Utils.getNewDateWithTimezone(
                        values.contractRenewal,
                        'Australia/Melbourne'
                      ).format('DD/MM/YYYY')
                    : ''
                }
              >
                <DatePickerInput
                  id="contractRenewal"
                  name="contractRenewal"
                  data-testid="contractRenewal"
                  labelText="Subscription end date"
                  placeholder="dd/mm/yyyy"
                  type="text"
                  autoComplete="off"
                  disabled={disableSubsriptionEndDate}
                />
              </DatePicker>
              {errors.contractRenewal && touched.contractRenewal && (
                <FormError>{errors.contractRenewal}</FormError>
              )}
            </div>
          </div>
          <div className="new-form-group">
            <div className="date-picker-container">
              <DatePicker
                dateFormat="d/m/Y"
                datePickerType="single"
                className="date-picker"
                minDate={subscriptionEndMin}
                onChange={(val: any) => {
                  onChangeDate('subscriptionEnd', val, true);
                }}
                value={
                  values.subscriptionEnd
                    ? Utils.getNewDateWithTimezone(
                        values.subscriptionEnd,
                        'Australia/Melbourne'
                      ).format('DD/MM/YYYY')
                    : ''
                }
              >
                <DatePickerInput
                  id="subscriptionEnd"
                  name="subscriptionEnd"
                  data-testid="subscriptionEnd"
                  labelText="Final contractual end date"
                  placeholder="dd/mm/yyyy"
                  type="text"
                  autoComplete="off"
                  disabled={disableContractRenewalDate}
                />
              </DatePicker>
              {errors.subscriptionEnd && touched.subscriptionEnd && (
                <FormError>{errors.subscriptionEnd}</FormError>
              )}
            </div>
          </div>
          <div className="new-form-group">
            <Toggle
              id="hasCreateEnquiry"
              size="sm"
              aria-label="Enable"
              labelA="Enable"
              labelB="Enable"
              labelText="BYDA Premium Features"
              checked={values.hasCreateEnquiry}
              onToggle={(show) => onCheckBoxChange(show)}
            />
          </div>
          <div className="limits-input-label">
            <FormLabel>Limits</FormLabel>
            <LimitTooltip align="start" direction="bottom" />
          </div>
          <div className="new-form-group limits-input">
            <NumberInput
              id="teamLimits"
              name="teamLimits"
              label="Number of teams"
              allowEmpty={true}
              max={MAX_LIMIT_MAX}
              min={MAX_LIMIT_MIN}
              step={MAX_LIMIT_STEP}
              invalidText={errors.teamLimits}
              invalid={touched.teamLimits && errors.teamLimits !== undefined}
              value={values.teamLimits || 0}
              onChange={(event: any) =>
                onNumberInputChange(
                  'teamLimits',
                  event.imaginaryTarget.value,
                  touched.teamLimits
                )
              }
              onBlur={handleBlur}
            />
            <NumberInput
              id="userLimits"
              name="userLimits"
              label="Number of users"
              allowEmpty={true}
              max={MAX_LIMIT_MAX}
              min={MAX_LIMIT_MIN}
              step={MAX_LIMIT_STEP}
              invalidText={errors.userLimits}
              invalid={touched.userLimits && errors.userLimits !== undefined}
              value={values.userLimits || 0}
              onChange={(event: any) =>
                onNumberInputChange(
                  'userLimits',
                  event.imaginaryTarget.value,
                  touched.userLimits
                )
              }
              onBlur={handleBlur}
            />
          </div>
          {isEditMode && (
            <>
              <div className="limits-input-label">
                <FormLabel>Transactions limits</FormLabel>
                <TransactionLimitTooltip align="center" direction="bottom" />
              </div>
              <div className="new-form-group limits-button">
                <Button
                  onClick={onOpenTransactionLimitsForm}
                  size="small"
                  renderIcon={Add16}
                  kind="tertiary"
                >
                  Add transaction limit
                </Button>
              </div>
            </>
          )}
        </div>
        <div className="footer">
          <Button
            onClick={() => {
              handleSubmit();
              setSubmitErrText('');
            }}
            disabled={
              Utils.isObjectEmpty(touched) ||
              !Utils.isObjectEmpty(errors) ||
              isSubmitting
            }
          >
            {createOrgsStatus === 'initial' && updateOrgStatus === 'initial' ? (
              'Save changes'
            ) : (
              <InlineLoading
                description={
                  createOrgsStatus === 'creating' ||
                  updateOrgStatus === 'updating'
                    ? 'Saving'
                    : createOrgsStatus === 'success' ||
                      updateOrgStatus === 'success'
                    ? 'Saved'
                    : 'Failed'
                }
                status={
                  createOrgsStatus === 'success' ||
                  updateOrgStatus === 'success'
                    ? 'finished'
                    : createOrgsStatus === 'failed' ||
                      updateOrgStatus === 'failed'
                    ? 'error'
                    : 'active'
                }
              />
            )}
          </Button>
          <Button
            kind="secondary"
            onClick={() => handleCancelClick()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

type Props = {
  onClose: () => void;
  onOpenTransactionLimitsForm: () => void;
  setToastNotificationData: Function;
  setShowNewOrgResponseModal: Function;
  setEditData: Function;
  editData: any;
};

export default React.memo(NewOrganisationForm);
